import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { getStripe, getStripeEnvironment, hasPaymentsConfigured } from "@/lib/stripe";
import { createCheckoutSession, redeemGiftCode } from "@/lib/payments.functions";
import { useAccess } from "@/hooks/use-access";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { isNativeApp } from "@/lib/native";
import { Sparkles, Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import logoHorizontal from "@/assets/wc-logo-horizontal-white.png.asset.json";

export const Route = createFileRoute("/_authenticated/paywall")({
  head: () => ({ meta: [{ title: "Continue Your Access — Boardroom" }] }),
  component: PaywallPage,
});

function PaywallPage() {
  const { user } = useAuth();
  const access = useAccess();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const [plan, setPlan] = useState<"monthly" | "annual">("annual");
  const [coupon, setCoupon] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const fetchClientSecret = async (): Promise<string> => {
    const returnUrl = `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`;
    const result = await createCheckoutSession({
      data: {
        returnUrl,
        environment: getStripeEnvironment(),
        couponCode: coupon.trim() || undefined,
        plan,
      },
    });
    if ("error" in result) throw new Error(result.error);
    if (!result.clientSecret) throw new Error("Stripe did not return a client secret");
    return result.clientSecret;
  };

  async function tryRedeem() {
    if (!coupon.trim()) return toast.error("Enter a code");
    setRedeeming(true);
    try {
      const res = await redeemGiftCode({ data: { code: coupon.trim() } });
      if ("granted" in res) {
        toast.success("Access granted!");
        // Full reload so the _authenticated layout re-mounts and its
        // useAccess re-reads the fresh access_status from the DB.
        window.location.href = "/dashboard";
        return;
      }
      // Non-100% or checkout-only codes → forward into embedded checkout with
      // the code pre-applied so the discount is visible before the user pays.
      if (res.error?.toLowerCase().includes("discount code")) {
        toast.info("Applying your discount at checkout…");
        setShowCheckout(true);
        return;
      }
      toast.error(res.error);
    } finally {
      setRedeeming(false);
    }
  }

  async function signOut() {
    const { fastSignOut } = await import("@/lib/sign-out");
    await fastSignOut();
  }

  const paymentsReady = hasPaymentsConfigured();
  const native = isNativeApp();

  // Inside the native App Store / Play Store shell the app is sign-in only:
  // memberships are sold via Stripe on the website, not in-app (Apple 3.1.1).
  if (native) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="border-b border-[color:var(--hairline)]">
          <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
            <img src={logoHorizontal.url} alt="Boardroom" className="h-7 w-auto" />
            <button
              onClick={signOut}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-2"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </header>
        <div className="max-w-xl mx-auto px-6 py-20 text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[color:var(--hairline)] text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            Manage your membership on the website
          </div>
          <h1 className="font-display text-4xl font-semibold">The Boardroom</h1>
          <p className="text-muted-foreground">
            Hi {user?.email}. Memberships are managed on our website. Visit
            {" "}
            <a
              href="https://boardroom.wealthycreatives.co"
              className="text-[color:var(--gold)] underline underline-offset-4"
            >
              boardroom.wealthycreatives.co
            </a>{" "}
            from any browser to start or update your membership, then sign back
            in here.
          </p>
          {access.status === "active" || access.status === "comped" ? (
            <button
              onClick={() => window.location.replace("/dashboard")}
              className="bg-[color:var(--gold)] text-black font-medium py-2.5 px-6 rounded-md hover:opacity-90 transition"
            >
              Go to Dashboard
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">
              Once your membership is active on the website, sign in here to
              open your dashboard.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-[color:var(--hairline)]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <img src={logoHorizontal.url} alt="Boardroom" className="h-7 w-auto" />
          <button
            onClick={signOut}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-2"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-16 space-y-10">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[color:var(--hairline)] text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            {access.status === "trial_expired" || access.status === "canceled"
              ? "Your access has ended"
              : access.status === "past_due"
                ? "Payment past due"
                : "Continue your Boardroom access"}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold">
            The Boardroom
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Hi {user?.email}. Your account exists — activate your membership to keep
            running your business from the Boardroom.
          </p>
        </div>

        {!showCheckout ? (
          <div className="grid md:grid-cols-[1.2fr_1fr] gap-6">
            <div className="boardroom-card p-8 space-y-5">
              <div className="flex items-baseline gap-2">
                <Sparkles className="h-5 w-5 text-[color:var(--gold)]" />
                <div className="text-sm uppercase tracking-widest text-muted-foreground">
                  Boardroom Membership
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPlan("monthly")}
                  className={`rounded-lg border p-4 text-left transition ${
                    plan === "monthly"
                      ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10"
                      : "border-[color:var(--hairline)] hover:border-[color:var(--gold)]/40"
                  }`}
                >
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Monthly</div>
                  <div className="font-display text-3xl font-semibold mt-1">$50</div>
                  <div className="text-xs text-muted-foreground">/ month</div>
                </button>
                <button
                  type="button"
                  onClick={() => setPlan("annual")}
                  className={`relative rounded-lg border p-4 text-left transition ${
                    plan === "annual"
                      ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10"
                      : "border-[color:var(--hairline)] hover:border-[color:var(--gold)]/40"
                  }`}
                >
                  <span className="absolute -top-2.5 right-3 rounded-full bg-[color:var(--gold)] px-2 py-0.5 text-[10px] font-semibold text-black">
                    2 months free
                  </span>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Annual</div>
                  <div className="font-display text-3xl font-semibold mt-1">$490</div>
                  <div className="text-xs text-muted-foreground">/ year</div>
                </button>
              </div>

              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>· Full access to every module</li>
                <li>· Revenue Pulse + Stripe integration</li>
                <li>· Proposal Pipeline</li>
                <li>· Daily Non-Negotiables with AI coaching</li>
                <li>· Cancel anytime from your account</li>
              </ul>
              <button
                onClick={() => setShowCheckout(true)}
                disabled={!paymentsReady}
                className="w-full bg-[color:var(--gold)] text-black font-medium py-3 rounded-md hover:opacity-90 transition disabled:opacity-40"
              >
                {paymentsReady
                  ? plan === "annual"
                    ? "Start annual membership"
                    : "Start monthly membership"
                  : "Checkout not available yet"}
              </button>
              {!paymentsReady && (
                <p className="text-xs text-muted-foreground text-center">
                  Payments are being finalized. Try again shortly, or use a code below.
                </p>
              )}
            </div>

            <div className="boardroom-card p-8 space-y-4">
              <div className="text-sm uppercase tracking-widest text-muted-foreground">
                Have a code?
              </div>
              <p className="text-sm text-muted-foreground">
                Client codes unlock lifetime access. Discount codes apply at
                checkout.
              </p>
              <input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                placeholder="YOUR-CODE"
                className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm uppercase tracking-wider"
              />
              <button
                onClick={tryRedeem}
                disabled={redeeming || !coupon.trim()}
                className="w-full border border-[color:var(--gold)] text-[color:var(--gold)] py-2.5 rounded-md hover:bg-[color:var(--gold)]/10 transition disabled:opacity-40"
              >
                {redeeming ? "Checking…" : "Redeem code"}
              </button>
              <p className="text-xs text-muted-foreground">
                Non-100% codes will pre-fill at checkout.
              </p>
            </div>
          </div>
        ) : (
          <div className="boardroom-card overflow-hidden">
            <div className="px-6 py-4 border-b border-[color:var(--hairline)] flex items-center justify-between">
              <div className="text-sm text-muted-foreground">Complete your subscription</div>
              <button
                onClick={() => setShowCheckout(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                ← Back
              </button>
            </div>
            <div id="checkout">
              <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
